import React, { useState, useEffect, useRef } from 'react';
import { Plus, Minus, Tag, Edit3, Check, X, Heart, ZoomIn, Zap, TrendingDown, ShoppingCart, Star, Award, Gift } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import PaymentPricesList from './PaymentPricesList';
import { BannerPreview } from '../pages/AdminBannerLayoutPage';

/* ── Product Detail Modal (replaces simple image zoom) ── */
const ProductDetailModal = ({ isOpen, onClose, product }) => {
  const { addToCart, cart, updateQuantity, getPriceForQuantity, toggleFavorite, isFavorite, storeSettings, getRawCartTotal, selectedPaymentMethod, resolveLineId, activeBranch } = useCart();
  const { user, getEffectiveCustomerType, getAdjustedPrice, getFinalUnitPrice } = useAuth();
  const effectiveType = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
  const isComercio = effectiveType === 'comercio';
  const isConsumerFinal = effectiveType === 'consumidor_final';
  const paymentMethodsForUser = storeSettings?.paymentMethods || [];
  // Solo COMERCIOS ven los precios por forma de pago en el detalle. Particular nunca.
  // Tampoco se muestran si el producto tiene descuento por cantidad (no se apilan).
  const hasQtyDiscount = !!(product?.quantityPrices && product.quantityPrices.length > 0);
  // SIEMPRE mostrar precios por forma de pago (aunque tenga descuento por cantidad).
  // El cliente debe conocer todos los precios disponibles para decidir cuánto comprar.
  const showPaymentPricesList = isComercio;
  // Consumidor final NO ve los tiers por cantidad.
  const showQuantityPricesUi = hasQtyDiscount && !isConsumerFinal;
  const { theme } = useTheme();
  const [qty, setQty] = useState(null); // null = "no tocado" · 0 = elegido explícitamente
  const [showBanner, setShowBanner] = useState(false);

  // 🔄 Reset qty local cada vez que el modal se cierra o cambia de producto,
  // sino un valor 0 elegido antes se sostiene entre aperturas y termina
  // borrando líneas del carrito por error (bug detectado en iter161).
  useEffect(() => {
    if (!isOpen) setQty(null);
  }, [isOpen, product?.id]);

  if (!isOpen || !product) return null;

  // 🏪 Branch-aware: la línea que buscamos es productId::sucursalActiva.
  const _lineId = resolveLineId ? resolveLineId(product.id) : product.id;
  const cartItem = cart.find(item => item.id === _lineId);
  const currentQty = cartItem ? cartItem.quantity : 0;
  const packSize = product.packSize || 1;
  const minQuantity = Math.max(1, Number(product.minQuantity) || 1);
  const basePrice = product.priceNumeric || 0;
  const hasQuantityPrices = product.quantityPrices && product.quantityPrices.length > 0;
  // Cliente con descuento fijo (permanente): el 6% es ÚNICO (compre lo que
  // compre) → ocultamos las ofertas por cantidad para no confundir.
  const _hasPermDiscCompact = Number(user?.permanentDiscountPct || 0) > 0;
  const isProductFavorite = isFavorite(product.id);
  const stock = product.stock >= 0 ? product.stock : -1;
  const showStockNumber = user?.role === 'admin' || user?.role === 'seller';
  const fmt = (n) => `$${(n || 0).toLocaleString('es-AR')}`;

  const displayQty = qty ?? (currentQty || minQuantity);
  const displayPrice = getPriceForQuantity(product, displayQty || 1);

  // Find current tier
  const getCurrentTier = (q) => {
    if (!hasQuantityPrices) return null;
    const sorted = [...product.quantityPrices].sort((a, b) => a.minQty - b.minQty);
    let tier = null;
    for (const t of sorted) { if (q >= t.minQty) tier = t; }
    return tier;
  };
  const activeTier = getCurrentTier(displayQty);

  const handleSetQty = (newQty) => {
    const v = Math.max(0, newQty);
    setQty(v);
  };

  const handleAddToCart = () => {
    let targetQty = qty ?? minQuantity;
    // 🚦 Snap a múltiplos de minQuantity. Valores entre 1 y (min-1) → 0.
    if (targetQty > 0 && targetQty < minQuantity) targetQty = 0;
    else if (targetQty > 0) targetQty = Math.round(targetQty / minQuantity) * minQuantity;
    let res;
    if (targetQty === 0) {
      if (currentQty > 0) updateQuantity(_lineId, 0);
      onClose();
      return;
    }
    if (currentQty === 0) res = addToCart(product, targetQty);
    else res = updateQuantity(_lineId, targetQty);
    if (res && !res.ok) {
      alert(res.message || 'No se puede agregar este producto.');
      return;
    }
    if (res && res.warning === 'low_stock') {
      console.warn(`[low-stock] ${product.name}: quedan ${res.stock}`);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl sm:mb-4 max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="product-detail-modal">
        {/* Image - compact on mobile (click to open ad-banner) */}
        <div className="relative bg-slate-50 p-4 flex items-center justify-center min-h-[150px] max-h-[180px] sm:min-h-[200px] sm:max-h-[220px] flex-shrink-0 rounded-t-2xl sm:rounded-t-2xl">
          <img src={product.imageUrl ? `${encodeURI(product.imageUrl)}?w=450` : ''} alt={product.name}
            onClick={() => setShowBanner(true)}
            className="max-h-[140px] sm:max-h-[180px] max-w-full object-contain cursor-zoom-in"
            data-testid={`open-banner-${product.id}`}
            onError={(e) => e.target.src = 'https://placehold.co/300x300/f8fafc/cbd5e1?text=Sin+imagen'} />
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm hover:bg-white">
            <X size={18} className="text-slate-600" />
          </button>
          <button onClick={() => toggleFavorite(product.id)} className="absolute top-3 left-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm">
            <Heart size={16} fill={isProductFavorite ? '#EF4444' : 'none'} stroke={isProductFavorite ? '#EF4444' : '#94a3b8'} />
          </button>
          {/* Badges */}
          <div className="absolute bottom-3 left-3 flex gap-1.5 flex-wrap">
            {product.destacado && (
              <span className="px-2 py-1 bg-amber-500 text-white text-[10px] font-black rounded-lg flex items-center gap-1"><Star size={10} /> DESTACADO</span>
            )}
            {hasQuantityPrices && !isConsumerFinal && !_hasPermDiscCompact && (
              <span className="px-2 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-black rounded-lg flex items-center gap-1"><TrendingDown size={10} /> DESCUENTO</span>
            )}
            {product.oferta && (
              <span className="px-2 py-1 bg-emerald-500 text-white text-[10px] font-black rounded-lg flex items-center gap-1"><Award size={10} /> OFERTA</span>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-2">
          {/* Name & description */}
          <h2 className="text-lg font-black text-slate-900 leading-tight mb-1" style={{ fontFamily: 'Barlow Condensed' }}>{product.name}</h2>
          {product.description && <p className="text-xs text-slate-500 mb-2">{product.description}</p>}
          {product.grupo && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{product.grupo}</span>}

          {/* Price */}
          <div className="mt-3 mb-2">
            {(() => {
              // 🎯 Precio con descuento del WINNER activo (cart-tier o permanente).
              // Solo aplicable si NO hay activeTier por cantidad (que ya tiene su
              // propia UI de tachado). Muestra basePrice tachado + displayPrice
              // real + badge -X%. Se re-calcula por cada render del modal.
              const rawTotal = typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0;
              const winnerFinal = getFinalUnitPrice ? getFinalUnitPrice(product, rawTotal, displayQty || 1, selectedPaymentMethod) : displayPrice;
              const basePriceCeil = Math.ceil(basePrice);
              const hasCartWinner = !activeTier && winnerFinal < basePriceCeil && basePriceCeil > 0;
              if (activeTier) {
                return (
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-black" style={{ color: '#D97706' }} data-testid={`modal-tier-price-${product.id}`}>{fmt(activeTier.priceNumeric)}</span>
                    <span className="text-base text-slate-400 line-through" data-testid={`modal-base-strike-${product.id}`}>{fmt(basePrice)}</span>
                    <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                      -{Math.round(((basePrice - activeTier.priceNumeric) / basePrice) * 100)}%
                    </span>
                  </div>
                );
              }
              if (hasCartWinner) {
                const savedPct = Math.round(((basePriceCeil - winnerFinal) / basePriceCeil) * 100);
                return (
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-black" style={{ color: '#D97706' }} data-testid={`modal-winner-price-${product.id}`}>{fmt(winnerFinal)}</span>
                    <span className="text-base text-slate-400 line-through" data-testid={`modal-base-strike-${product.id}`}>{fmt(basePrice)}</span>
                    <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full" data-testid={`modal-discount-badge-${product.id}`}>-{savedPct}%</span>
                  </div>
                );
              }
              return (
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black text-slate-900">{fmt(displayPrice)}</span>
                </div>
              );
            })()}
            <p className="text-[10px] text-slate-400 mt-0.5">
              {packSize > 1 ? `por unidad (pack x${packSize})` : 'por unidad'}
            </p>
          </div>

          {/* Quantity Prices Table — oculto para consumidor final */}
          {showQuantityPricesUi && (
            <div className="mb-3 rounded-xl border border-amber-200 overflow-hidden" data-testid="modal-qty-tiers">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 flex items-center gap-1.5">
                <Zap size={12} className="text-white" />
                <span className="text-[10px] text-white font-black uppercase tracking-wider">Precios por cantidad</span>
              </div>
              {product.quantityPrices.map((tier, idx) => {
                const isActive = activeTier && activeTier.minQty === tier.minQty;
                const saving = basePrice - tier.priceNumeric;
                const pct = basePrice > 0 ? Math.round((saving / basePrice) * 100) : 0;
                return (
                  <div key={idx} className={`flex items-center justify-between px-3 py-2 border-b border-amber-50 last:border-0 ${isActive ? 'bg-amber-50' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-black ${isActive ? 'text-amber-700' : 'text-slate-600'}`}>{tier.minQty}+ uds</span>
                      {isActive && <span className="text-[8px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold">ACTIVO</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-black ${isActive ? 'text-amber-700' : 'text-slate-700'}`}>{fmt(tier.priceNumeric)}</span>
                      {pct > 0 && <span className="text-[9px] font-bold text-red-500">-{pct}%</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Otros precios por forma de pago — se calculan siempre sobre el
              precio de lista base (no sobre el tier), aunque el cliente esté
              aprovechando un descuento por cantidad. Así queda visible el
              precio si compra menos cantidad y paga en efectivo. */}
          {showPaymentPricesList && (
            <PaymentPricesList
              basePrice={getFinalUnitPrice ? getFinalUnitPrice(product, (typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0), 1, selectedPaymentMethod) : (getAdjustedPrice ? getAdjustedPrice(basePrice, 0) : basePrice)}
              paymentMethods={paymentMethodsForUser}
              allowedRestricted={user?.allowedPaymentMethods}
              variant="full"
            />
          )}

          {/* Banner Tags */}
          {product.bannerTags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {product.bannerTags.map((tag, idx) => (
                <span key={idx} className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: tag.color || '#6366f1', color: tag.textColor || '#fff' }}>
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Sticky Footer - Quantity + Add to Cart */}
        <div className="flex-shrink-0 border-t border-slate-100 p-4 bg-white rounded-b-2xl">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => handleSetQty(Math.max(0, displayQty - minQuantity))}
              className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors"
              data-testid="modal-qty-decrease">
              <Minus size={18} />
            </button>
            <input type="number" value={displayQty}
              onChange={(e) => handleSetQty(parseInt(e.target.value) || 0)}
              className="flex-1 h-10 text-center text-lg font-black border-2 border-slate-200 rounded-xl focus:border-orange-400 outline-none max-w-[80px]"
              min={0} step={minQuantity} data-testid="modal-qty-input" />
            <button onClick={() => handleSetQty(displayQty + minQuantity)}
              className="w-10 h-10 rounded-xl text-white flex items-center justify-center transition-opacity hover:opacity-90"
              style={{ backgroundColor: theme.primary }}
              data-testid="modal-qty-increase">
              <Plus size={18} />
            </button>
            <div className="flex-1 text-right">
              <p className="text-sm font-black text-slate-800">{fmt(displayPrice * displayQty)}</p>
              {showQuantityPricesUi && !activeTier && displayQty < (product.quantityPrices[0]?.minQty || 999) && (
                <p className="text-[9px] text-amber-600">+{(product.quantityPrices[0]?.minQty || 0) - displayQty} para dto!</p>
              )}
              {minQuantity > 1 && (
                <p className="text-[9px] text-slate-500 mt-0.5" data-testid={`modal-min-${product.id}`}>Mínimo: {minQuantity} un.</p>
              )}
            </div>
          </div>
          <button onClick={handleAddToCart}
            className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98] shadow-lg"
            style={{ backgroundColor: theme.primary }}
            data-testid="modal-add-to-cart">
            <ShoppingCart size={18} />
            {currentQty > 0 ? 'Actualizar carrito' : 'Agregar al carrito'}
          </button>
          {currentQty > 0 && (
            <p className="text-center text-[10px] text-slate-400 mt-1.5">Ya tenes {currentQty} en el carrito</p>
          )}
        </div>
      </div>
      {showBanner && <ProductBannerOverlay product={product} onClose={() => setShowBanner(false)} fmt={fmt} />}
    </div>
  );
};

/* ── Product Banner Overlay (publicidad full screen para compartir) ── */
const DEFAULT_BANNER_LAYOUT = {
  logo: { top: 1.5, left: 4, width: 32 },
  image: { top: 1, right: 1, width: 48, height: 60 },
  text: { top: 30, left: 5, width: 56, maxHeight: 60 },
  namePx: 28, pricePx: 60, lineHeight: 1.05,
  nameLines: 3, showFooter: true, footerText: '📲 Pedilo por WhatsApp',
  bgColor: '#ffffff', nameColor: '#EC4899', priceColor: '#06B6D4', accentColor: '#FB923C',
  footerBgColor: '#ffffff', footerTextColor: '#404040',
  showDiscountBadge: true, showOfertaBadge: true, showDestacadoBadge: true, showTierBadge: true,
  topText: '', topTextColor: '#EF4444', topTextPx: 24,
  borderWidth: 0, borderColor: '#EC4899',
  showCatalogUrl: false, catalogUrlText: 'bebida-bacana.com', catalogUrlPx: 11,
};

const ProductBannerOverlay = ({ product, onClose, fmt }) => {
  const cardRef = React.useRef(null);
  const { storeSettings } = useCart();
  const { user, getEffectiveCustomerType } = useAuth();
  const [busy, setBusy] = useState(false);
  // Solo administradores y vendedores pueden compartir/descargar el banner.
  // Admin se detecta por flag de localStorage (login admin separado del Google Auth).
  const isLocalAdmin = typeof window !== 'undefined' && localStorage.getItem('bebida-bacana-admin') === 'true';
  const canDownload = isLocalAdmin || user?.role === 'admin' || user?.role === 'seller';
  const effectiveType = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
  // Mostrar precios por forma de pago a: COMERCIOS o staff/admin (para descargar banner).
  // Consumidor final NUNCA ve descuentos por forma de pago.
  // Productos con descuento por cantidad tampoco — no se apilan.
  const _hasQtyDiscountBanner = !!(product?.quantityPrices && product.quantityPrices.length > 0);
  // SIEMPRE mostrar los precios por forma de pago en el banner. Antes se ocultaban
  // si había descuento por cantidad, pero el cliente necesita conocer todos los
  // descuentos disponibles (efectivo, retirando) para decidir cuánto comprar.
  const showPaymentPrices = (effectiveType === 'comercio' || canDownload);
  // Lista de métodos según tipo (B2B vs B2C). El admin/staff ve la lista B2B por defecto
  // ya que comparte el banner con comercios.
  const _bannerPaymentMethods = effectiveType === 'consumidor_final'
    ? (storeSettings?.consumerPaymentMethods || [])
    : (storeSettings?.paymentMethods || []);

  // Layout viene de storeSettings.bannerLayout (editable desde admin) con defaults seguros.
  const layout = React.useMemo(() => {
    const L = storeSettings?.bannerLayout || {};
    return {
      ...DEFAULT_BANNER_LAYOUT,
      ...L,
      logo: { ...DEFAULT_BANNER_LAYOUT.logo, ...(L.logo || {}) },
      image: { ...DEFAULT_BANNER_LAYOUT.image, ...(L.image || {}) },
      text: { ...DEFAULT_BANNER_LAYOUT.text, ...(L.text || {}) },
    };
  }, [storeSettings]);

  // Helper: pide al backend el PNG server-side del banner.
  // Formato ÚNICO: ig-story (1080×1920, 9:16) — Instagram Story y Estado de WhatsApp.
  // Retry automático ante 502/503/504 (el render con Playwright puede tardar
  // más si el browser está frío o el backend acaba de reiniciar).
  const fetchBannerBlob = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/broadcast-assets/banner/${encodeURIComponent(product.id)}.png?format=ig-story&t=${Date.now()}`;
    const isRetryable = (status) => status === 502 || status === 503 || status === 504;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const r = await fetch(url, { cache: 'no-cache' });
        if (r.ok) return await r.blob();
        if (!isRetryable(r.status) || attempt === 3) {
          throw new Error(`No se pudo generar el banner. HTTP ${r.status}`);
        }
      } catch (e) {
        if (attempt === 3) throw e;
      }
      // Backoff exponencial: 1.5s, 3s
      await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
    }
  };

  const share = async (e) => {
    // CRITICAL: el overlay padre tiene onClick={onClose}.
    // Sin stopPropagation, al hacer click en el botón se cierra antes de procesar.
    if (e && e.stopPropagation) e.stopPropagation();
    setBusy(true);
    const publicUrl = `${process.env.REACT_APP_BACKEND_URL}/api/broadcast-assets/banner/${encodeURIComponent(product.id)}.png`;
    const shareText = `${product.name} — ${fmt(product.priceNumeric || product.price || 0)}\n${publicUrl}`;
    try {
      const blob = await fetchBannerBlob();
      const file = new File([blob], `${product.name.replace(/\s+/g, '_')}.png`, { type: 'image/png' });

      // Intento 1: Web Share API CON archivo (móvil moderno, HTTPS)
      if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: product.name,
            text: `${product.name} — ${fmt(product.priceNumeric || product.price || 0)}`,
          });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return; // user canceled
          console.warn('share with file failed, trying text only:', err);
        }
      }

      // Intento 2: Web Share API SIN archivo (text/URL pública del PNG)
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({ title: product.name, text: shareText, url: publicUrl });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
          console.warn('share text failed:', err);
        }
      }

      // Intento 3 (desktop): copiar URL al portapapeles + descargar el PNG
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(publicUrl);
        }
      } catch (_) { /* noop */ }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
      alert('Listo: imagen descargada y link copiado al portapapeles.\nPegá el link en WhatsApp para compartir.');
    } catch (err) {
      console.error('share error', err);
      alert('Error al generar la imagen: ' + (err && err.message ? err.message : err));
    } finally {
      setBusy(false);
    }
  };

  const downloadOnly = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setBusy(true);
    try {
      const blob = await fetchBannerBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${product.name.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
    } catch (err) {
      console.error('download error', err);
      alert('Error al descargar: ' + (err && err.message ? err.message : err));
    } finally { setBusy(false); }
  };

  // Backward-compat: el ref del preview visual; ya no se usa para captura.


  const storeName = storeSettings?.storeName || 'Bebida Bacana';
  const price = product.priceNumeric || product.price || 0;
  const tiers = (product.quantityPrices || []).slice().sort((a, b) => a.minQty - b.minQty);
  const bestTier = tiers.length ? tiers[tiers.length - 1] : null;
  const bestSavingsPct = bestTier && price > 0 ? Math.round(((price - bestTier.priceNumeric) / price) * 100) : 0;

  // Usar la imagen ORIGINAL (con fondo). Es más rápida de cargar y no rompe el catálogo.
  // Para que el fondo no tape el contenido, el texto/logo/precio van con z-index mayor.
  const getOriginalUrl = (url) => {
    if (!url) return '';
    return encodeURI(url);
  };

  // Formato ÚNICO: 9:16 (Instagram Story / Estados WhatsApp).
  const FMT_DIMS = { 'ig-story': { ratio: '9 / 16', width: 'min(430px, 85vw)' } };
  const fmtKey = 'ig-story';
  const dims = FMT_DIMS[fmtKey];

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="product-banner-overlay">
      <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white" data-testid="banner-close">
        <X size={22} />
      </button>
      <div onClick={(e) => e.stopPropagation()}
        ref={cardRef}
        className="relative rounded-3xl overflow-hidden"
        style={{ width: dims.width }}
      >
        {/* Componente UNIFICADO: mismo que usa /admin/banner-layout y BannerRenderPage
            (Playwright captura este mismo componente vía BannerRenderPage). Así
            el preview en el catálogo, el preview del editor y el PNG descargado
            coinciden PIXEL-PERFECT. */}
        <BannerPreview
          product={{ ...product, priceNumeric: price }}
          layout={layout}
          storeSettings={{
            ...storeSettings,
            // Forzamos los payment methods correctos según tipo de usuario
            // (para que el chip de descuento se pinte igual que en la descarga).
            paymentMethods: showPaymentPrices ? _bannerPaymentMethods : [],
          }}
          fmt={fmtKey}
          previewW={540}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-2 max-w-md" onClick={(e) => e.stopPropagation()}>
        {canDownload ? (
          <>
            <button onClick={share} disabled={busy}
              className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-full font-bold flex items-center gap-2 text-sm"
              data-testid="banner-share">
              📲 {busy ? 'Generando...' : 'Compartir'}
            </button>
            <button onClick={downloadOnly} disabled={busy}
              className="px-5 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-full font-bold flex items-center gap-2 text-sm"
              data-testid="banner-download">
              📥 Descargar
            </button>
          </>
        ) : (
          <button onClick={onClose}
            className="px-5 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-full font-bold flex items-center gap-2 text-sm"
            data-testid="banner-close">
            Cerrar
          </button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-white/60 text-center px-6">
        {canDownload
          ? 'Ideal para compartir en WhatsApp, Instagram o Facebook'
          : 'Vista ampliada del producto'}
      </p>
    </div>
  );
};

const SparkleDot = ({ style, color = '#0F172A' }) => (
  <svg className="absolute pointer-events-none z-20" style={{ overflow: 'visible', ...style }} viewBox="0 0 24 24" aria-hidden="true">
    {/* 4-point sparkle/star */}
    <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z"
      fill={color} stroke={color} strokeWidth="0.5" strokeLinejoin="round" />
  </svg>
);

/* ── Confetti Particle ── */
const ConfettiParticle = ({ delay, color }) => {
  const style = {
    position: 'absolute',
    width: `${Math.random() * 8 + 4}px`,
    height: `${Math.random() * 8 + 4}px`,
    backgroundColor: color,
    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
    left: `${Math.random() * 100}%`,
    top: '-10px',
    animation: `confetti-fall 1.5s ease-out ${delay}s forwards`,
    opacity: 0,
    transform: `rotate(${Math.random() * 360}deg)`,
    zIndex: 60,
    pointerEvents: 'none'
  };
  return <div style={style} />;
};

/* ── Discount Celebration Overlay ── */
const DiscountCelebration = ({ show, tierPrice, savings, onClose }) => {
  useEffect(() => {
    if (show) {
      const t = setTimeout(onClose, 3200);
      return () => clearTimeout(t);
    }
  }, [show, onClose]);

  if (!show) return null;

  const colors = ['#F59E0B', '#EF4444', '#10B981', '#6366F1', '#EC4899', '#F97316'];
  const fmt = (n) => `$${(n || 0).toLocaleString('es-AR')}`;

  return (
    <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden rounded-xl">
      {/* Confetti */}
      {Array.from({ length: 24 }).map((_, i) => (
        <ConfettiParticle key={i} delay={Math.random() * 0.5} color={colors[i % colors.length]} />
      ))}
      {/* Banner */}
      <div className="absolute inset-x-0 top-1/3 flex items-center justify-center animate-discount-pop pointer-events-none">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-4 py-2.5 rounded-xl shadow-2xl text-center max-w-[90%]" style={{ animation: 'discount-glow 1.5s ease-in-out infinite' }}>
          <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Descuento Activado</p>
          <p className="text-lg font-black leading-tight">{fmt(tierPrice)} c/u</p>
          {savings > 0 && <p className="text-xs font-bold opacity-90">Ahorraste {fmt(savings)} por unidad</p>}
        </div>
      </div>
    </div>
  );
};

/* ── CSS Animations (injected once) ── */
const styleId = 'discount-animations';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes confetti-fall {
      0% { opacity: 1; transform: translateY(0) rotate(0deg) scale(0); }
      20% { opacity: 1; transform: translateY(20px) rotate(90deg) scale(1.2); }
      100% { opacity: 0; transform: translateY(180px) rotate(720deg) scale(0.3); }
    }
    @keyframes discount-pop {
      0% { transform: scale(0) rotate(-10deg); opacity: 0; }
      50% { transform: scale(1.15) rotate(2deg); opacity: 1; }
      70% { transform: scale(0.95) rotate(-1deg); opacity: 1; }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }
    @keyframes discount-glow {
      0%, 100% { box-shadow: 0 0 20px rgba(245, 158, 11, 0.4); }
      50% { box-shadow: 0 0 40px rgba(245, 158, 11, 0.8), 0 0 60px rgba(239, 68, 68, 0.3); }
    }
    @keyframes tier-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .animate-discount-pop { animation: discount-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
    .animate-tier-pulse { animation: tier-pulse 0.4s ease-out; }
    .tier-active { background: linear-gradient(135deg, #F59E0B, #EF4444) !important; color: white !important; transform: scale(1.08); box-shadow: 0 4px 15px rgba(245, 158, 11, 0.5); }
    .tier-next { border: 2px dashed #F59E0B; opacity: 0.7; }
    @keyframes slideUp {
      from { transform: translateY(100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

const ProductCard = ({ product, showBadges = true, isLarge = false }) => {
  const { addToCart, cart, updateQuantity, getPriceForQuantity, toggleFavorite, isFavorite, storeSettings, getRawCartTotal, selectedPaymentMethod, resolveLineId } = useCart();
  const { theme } = useTheme();
  const { user, getEffectiveCustomerType, getListedPrice, getAdjustedPrice, getBestDiscountPct, getWinnerDiscountPct, getFinalUnitPrice, isFeatureEnabled, settingsReady } = useAuth();
  const effectiveType = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
  const isComercio = effectiveType === 'comercio';
  const isConsumerFinal = effectiveType === 'consumidor_final';
  // 🚦 Feature flags — determinan si mostrar los tiers por cantidad y los
  // precios por forma de pago. Si el flag está OFF, el ProductCard se
  // simplifica: sin banner "Oferta por cantidad", sin selector de tiers,
  // sin lista de "Precios por forma de pago".
  const ffQtyDisc = isFeatureEnabled ? isFeatureEnabled('productQuantityDiscounts') : true;
  const ffPmDisc = isFeatureEnabled ? isFeatureEnabled('paymentMethodDiscounts') : true;
  const paymentMethodsForUser = storeSettings?.paymentMethods || [];
  const hasQtyDiscount = ffQtyDisc && !!(product?.quantityPrices && product.quantityPrices.length > 0);
  const showPaymentPricesList = isComercio && ffPmDisc;
  const showQuantityPricesUi = hasQtyDiscount && !isConsumerFinal;
  const [editingQty, setEditingQty] = useState(false);
  const [inputQty, setInputQty] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [stockWarning, setStockWarning] = useState(false);
  const [stockWarningMsg, setStockWarningMsg] = useState('');
  const prevTierRef = useRef(null);
  
  const isLocalAdmin = typeof window !== 'undefined' && localStorage.getItem('bebida-bacana-admin') === 'true';
  const isStaff = isLocalAdmin || user?.role === 'admin' || user?.role === 'seller';
  const isProductFavorite = isFavorite(product.id);
  const hasBannerTags = product.bannerTags && product.bannerTags.length > 0;
  const stock = product.stock !== undefined && product.stock !== null && product.stock >= 0 ? product.stock : -1;
  const isOutOfStock = stock === 0;
  const isLowStock = stock > 0 && stock <= 6;

  const cartItem = cart.find(item => item.id === (resolveLineId ? resolveLineId(product.id) : product.id));
  const currentQty = cartItem ? cartItem.quantity : 0;
  const currentPrice = getPriceForQuantity(product, currentQty || 1);
  const packSize = product.packSize || 1;
  const minQuantity = Math.max(1, Number(product.minQuantity) || 1);
  const isPack = packSize > 1;
  const hasMinimum = minQuantity > 1;
  // 🚦 Cuando el flag de descuentos por cantidad está OFF, ignoramos
  // `product.quantityPrices` para todos los cálculos (tier, "Agregá X más y
  // ahorrás", strikethrough, etc.). El precio final se calcula por winner
  // vs cart-tier + permanente en getFinalUnitPrice.
  const hasQuantityPrices = ffQtyDisc && !!(product.quantityPrices && product.quantityPrices.length > 0);
  const basePrice = product.priceNumeric || 0;
  // 🏪 Id de línea branch-aware para +/- (mantiene la sucursal activa).
  const _lineId = resolveLineId ? resolveLineId(product.id) : product.id;

  // 🔢 Lógica de MÚLTIPLOS DEL MÍNIMO (2026-09-04 pm-3):
  //   - `minQuantity` es simultáneamente la cantidad mínima Y el paso de
  //     incremento/decremento. Ej: min=3 → 0,3,6,9,12...
  //   - `packSize` (caja/bulto) se ignora para el flujo del cliente; sólo
  //     sirve para inventario/proveedores. Lo mantenemos en el modelo pero
  //     NO se usa como step del selector.
  const firstAddQty = (stock >= 0 && stock < minQuantity && stock > 0) ? stock : minQuantity;
  const stepQty = minQuantity;

  // Detect current tier
  const getCurrentTier = (qty) => {
    if (!hasQuantityPrices) return null;
    const sorted = [...product.quantityPrices].sort((a, b) => a.minQty - b.minQty);
    let tier = null;
    for (const t of sorted) {
      if (qty >= t.minQty) tier = t;
    }
    return tier;
  };

  // Si el método de pago seleccionado bloquea descuentos por volumen, NO
  // aplicamos tier por cantidad — el precio y la UI de tier se ocultan.
  const _pmBlocksVolume = selectedPaymentMethod && selectedPaymentMethod.allowVolumeDiscounts === false;
  const _rawCurrentTier = getCurrentTier(currentQty);
  // 🧷 Si el cliente tiene descuento fijo (permanente), ese % es ÚNICO: no se
  // muestra el tier por cantidad (el precio ya viene con el 6% vía getFinalUnitPrice).
  const _hasPermDisc = Number(user?.permanentDiscountPct || 0) > 0;
  const currentTier = (_pmBlocksVolume || _hasPermDisc) ? null : _rawCurrentTier;

  // Detect tier change and trigger celebration
  useEffect(() => {
    if (!hasQuantityPrices || !product.quantityPrices) return;
    const sorted = [...product.quantityPrices].sort((a, b) => a.minQty - b.minQty);
    let tier = null;
    for (const t of sorted) {
      if (currentQty >= t.minQty) tier = t;
    }
    const prevTier = prevTierRef.current;
    if (tier && (!prevTier || tier.minQty > prevTier.minQty) && currentQty > 0) {
      const savings = basePrice - tier.priceNumeric;
      setCelebration({ tierPrice: tier.priceNumeric, savings: savings > 0 ? savings : 0 });
    }
    prevTierRef.current = tier;
  }, [currentQty, hasQuantityPrices, product.quantityPrices, basePrice]);

  const handleAdd = () => {
    let res;
    if (currentQty === 0) res = addToCart(product, firstAddQty);
    else {
      const newQty = currentQty + stepQty;
      res = updateQuantity(_lineId, newQty);
    }
    if (res && !res.ok) {
      // Bloqueo real por stock — único caso en que mostramos el cartel
      setStockWarningMsg(res.message || 'Ya no quedan unidades disponibles');
      setStockWarning(true);
      setTimeout(() => setStockWarning(false), 4000);
      return;
    }
  };
  const handleRemove = () => {
    if (currentQty <= 0) return;
    // Al bajar, si quedamos por debajo del mínimo eliminamos la línea
    // (no dejamos 3 unidades cuando el mínimo es 6).
    const next = currentQty - stepQty;
    if (next < minQuantity) updateQuantity(_lineId, 0);
    else updateQuantity(_lineId, next);
  };
  const handleSaveQty = () => {
    const qty = parseInt(inputQty) || 0;
    // 🚦 Snap a múltiplos de `minQuantity`. Cualquier valor entre 1 y
    // (min-1) → elimina la línea (0). Los demás se redondean al múltiplo
    // más cercano (ej. min=3, escribir 4 → 3; escribir 5 → 6; escribir 8 → 9).
    let target = 0;
    if (qty > 0) {
      if (qty < minQuantity) target = 0;
      else target = Math.round(qty / minQuantity) * minQuantity;
    }
    updateQuantity(_lineId, target);
    setEditingQty(false);
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('es-AR')}`;

  return (
    <>
      <ProductDetailModal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} product={product} />
      {showImageModal && (
        <ProductBannerOverlay product={product} onClose={() => setShowImageModal(false)} fmt={fmt} />
      )}
      <div data-testid={`product-card-${product.id}`}
        className={`bg-white rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg flex flex-col relative ${
          hasBannerTags ? 'ring-1 ring-amber-200 shadow-md' : 'border border-slate-200 shadow-sm'
        } ${celebration ? 'ring-2 ring-amber-400' : ''}`}>
        
        {/* Celebration overlay */}
        <DiscountCelebration
          show={!!celebration}
          tierPrice={celebration?.tierPrice}
          savings={celebration?.savings}
          onClose={() => setCelebration(null)}
        />

        {/* Stock warning overlay */}
        {stockWarning && (
          <div className="absolute inset-x-0 bottom-0 z-40 p-2 animate-[slideUp_0.3s_ease-out]" style={{ animation: 'slideUp 0.3s ease-out' }}>
            <div className="bg-amber-500 text-white text-[11px] font-bold px-3 py-2 rounded-lg shadow-lg text-center">
              {stockWarningMsg || 'Ya sin stock 😕'}
            </div>
          </div>
        )}

        {/* Image — click directo abre la imagen publicitaria para compartir */}
        <div className='relative bg-slate-50/50 p-2'>
          <div className={`w-full flex items-center justify-center cursor-pointer group ${hasBannerTags || isLarge ? 'h-48 sm:h-56' : 'h-28 sm:h-36'}`}
            onClick={() => setShowImageModal(true)}
            data-testid={`open-banner-${product.id}`}>
            <img src={product.imageUrl ? `${encodeURI(product.imageUrl)}?w=450` : ''} alt={product.name}
              className={`max-h-full object-contain transition-transform duration-300 group-hover:scale-105 ${hasBannerTags || isLarge ? 'max-w-[85%]' : 'max-w-full'}`}
              data-testid={`product-image-${product.id}`} loading="lazy"
              onError={(e) => e.target.src = 'https://placehold.co/160x160/f8fafc/cbd5e1?text=Sin+imagen'} />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5 rounded-lg">
              <span className="text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 px-2.5 py-1 rounded-lg shadow-md flex items-center gap-1">
                {isStaff ? '📲 Compartir' : '🔍 Ver imagen'}
              </span>
            </div>
          </div>
          {/* Botón pequeño "Ver detalle" debajo del Heart */}
          <button onClick={(e) => { e.stopPropagation(); setShowDetailModal(true); }}
            className="absolute bottom-2 right-2 px-2 py-0.5 bg-white/95 backdrop-blur-sm shadow-sm rounded-full text-[10px] font-bold text-slate-600 hover:bg-white"
            data-testid={`open-detail-${product.id}`}>
            Ver detalle
          </button>
          {/* Fav */}
          <button onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
            className='absolute top-2 left-2 p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm transition-all hover:scale-110'
            title={isProductFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}>
            <Heart size={14} fill={isProductFavorite ? '#EF4444' : 'none'} stroke={isProductFavorite ? '#EF4444' : '#CBD5E1'} />
          </button>
          {/* Badge de promoción (2+1 / regalo) */}
          {product.promo && (
            <div className={`absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg shadow-md text-white ${product.promo.tipo === '2x1' ? 'bg-gradient-to-r from-emerald-500 to-green-600' : 'bg-gradient-to-r from-fuchsia-500 to-pink-600'}`}
              title={product.promo.descripcion} data-testid={`promo-badge-${product.id}`}>
              <Gift size={12} />
              <span className='text-[10px] font-black tracking-wide'>{product.promo.label}</span>
            </div>
          )}
          {/* Discount badge top-right — oculto para consumidor final */}
          {hasQuantityPrices && !isConsumerFinal && (
            <div className='absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-1 rounded-lg shadow-md flex items-center gap-1'>
              <TrendingDown size={11} />
              <span className='text-[10px] font-black tracking-wide'>DESCUENTO</span>
            </div>
          )}
        </div>
      
        {/* Banner Tags */}
        {showBadges && hasBannerTags && (
          <div className='flex flex-wrap gap-1 px-3 pt-2'>
            {product.bannerTags.map((tag, idx) => (
              <span key={idx} className='text-[10px] font-medium px-2 py-0.5 rounded-full' style={{ backgroundColor: tag.color || '#6366f1', color: tag.textColor || '#ffffff' }}>
                {tag.name}
              </span>
            ))}
          </div>
        )}
        {/* Descripción de la promoción */}
        {product.promo && (
          <div className={`mx-3 mt-2 flex items-start gap-1.5 px-2 py-1.5 rounded-lg border ${product.promo.tipo === '2x1' ? 'bg-emerald-50 border-emerald-200' : 'bg-fuchsia-50 border-fuchsia-200'}`}
            data-testid={`promo-desc-${product.id}`}>
            <Gift size={13} className={product.promo.tipo === '2x1' ? 'text-emerald-600 shrink-0 mt-0.5' : 'text-fuchsia-600 shrink-0 mt-0.5'} />
            <span className={`text-[10px] font-semibold leading-tight ${product.promo.tipo === '2x1' ? 'text-emerald-700' : 'text-fuchsia-700'}`}>{product.promo.descripcion}</span>
          </div>
        )}
        {/* Stock indicator — visible para todos para no vender lo que no hay */}
        {isOutOfStock && (
          <div className='mx-3 mt-2 px-2 py-1 bg-rose-50 border border-rose-200 rounded-lg text-center' data-testid={`out-of-stock-${product.id}`}>
            <span className='text-[11px] font-bold text-rose-600'>SIN STOCK</span>
          </div>
        )}
        {isPack && showBadges && (
          <div className='px-3 pt-1.5'>
            <span className='text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500'>Pack x{packSize}</span>
          </div>
        )}
      
        {/* Content */}
        <div className='p-3 flex flex-col flex-1'>
          <h3 className='font-bold text-sm sm:text-base text-slate-800 line-clamp-2 mb-1 leading-snug' style={{ fontFamily: 'Barlow Condensed' }} data-testid={`product-name-${product.id}`} translate="no">
            {product.name}
          </h3>
          {hasMinimum && (
            <p
              className='text-[10px] font-semibold text-slate-500 -mt-0.5 mb-1 tracking-wide'
              data-testid={`product-min-${product.id}`}
            >
              Mínimo: {minQuantity} un.
            </p>
          )}
          <p className='text-xs text-slate-400 line-clamp-2 mb-2 flex-1' data-testid={`product-description-${product.id}`}>
            {product.description}
          </p>

          {/* ── Quantity Price Tiers — oculto para consumidor final ── */}
          {showQuantityPricesUi && (
            <div className='mb-3 rounded-xl overflow-hidden border border-amber-200' data-testid={`qty-tiers-${product.id}`}>
              <div className='bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 flex items-center gap-1.5'>
                <Zap size={12} className='text-white' />
                <span className='text-[10px] text-white font-black uppercase tracking-wider'>Mas llevas, menos pagas</span>
              </div>
              <div className='grid gap-0'>
                {/* Base price row */}
                <div className={`flex items-center justify-between px-3 py-1.5 text-xs transition-all duration-300 ${
                  !currentTier && currentQty > 0 ? 'bg-slate-100 font-bold' : 'bg-white'
                }`}>
                  <span className='text-slate-500'>Precio base</span>
                  <span className='font-bold text-slate-600'>{fmt(basePrice)}</span>
                </div>
                {product.quantityPrices.map((tier, idx) => {
                  const isActive = currentTier && currentTier.minQty === tier.minQty;
                  const saving = basePrice - tier.priceNumeric;
                  const pct = basePrice > 0 ? Math.round((saving / basePrice) * 100) : 0;
                  const isNext = !isActive && currentQty < tier.minQty && (!currentTier || tier.minQty > currentTier.minQty);
                  const unitsNeeded = tier.minQty - currentQty;
                  return (
                    <div key={idx} className={`flex items-center justify-between px-3 py-2 transition-all duration-500 ${
                      isActive ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500' : 
                      isNext ? 'bg-slate-50 border-l-4 border-dashed border-amber-300' : 'bg-white border-l-4 border-transparent'
                    } ${isActive ? 'animate-tier-pulse' : ''}`}
                    data-testid={`tier-${tier.minQty}`}>
                      <div className='flex items-center gap-2'>
                        <span className={`text-sm font-black ${isActive ? 'text-amber-700' : isNext ? 'text-slate-500' : 'text-slate-400'}`}>
                          {tier.minQty}+
                        </span>
                        {isActive && <span className='text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold animate-bounce'>ACTIVO</span>}
                        {isNext && currentQty > 0 && (
                          <span className='text-[9px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-bold'>
                            Faltan {unitsNeeded}
                          </span>
                        )}
                      </div>
                      <div className='text-right'>
                        <span className={`text-sm font-black ${isActive ? 'text-amber-700' : 'text-slate-700'}`}>
                          {fmt(tier.priceNumeric)}
                        </span>
                        {saving > 0 && (
                          <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            isActive ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500'
                          }`}>
                            -{pct}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Otros precios por forma de pago — visible para comercio y consumidor final */}
          {showPaymentPricesList && (
            <PaymentPricesList
              basePrice={getFinalUnitPrice ? getFinalUnitPrice(product, (typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0), currentQty || 1, selectedPaymentMethod) : (getAdjustedPrice ? getAdjustedPrice(currentTier ? currentTier.priceNumeric : basePrice, 0) : (currentTier ? currentTier.priceNumeric : basePrice))}
              paymentMethods={paymentMethodsForUser}
              allowedRestricted={user?.allowedPaymentMethods}
              variant="compact"
            />
          )}

          {/* Price & Quantity — apiladas en 2 filas para que el precio nunca
              se corte ni empuje los botones +/- (antes compartían una sola
              fila horizontal y en mobile con descuento activo se recortaban) */}
          <div className='mt-auto'>
            <div className='mb-2'>
              {(() => {
                if (currentTier) return null;
                // 🟡 Settings de la tienda (cartDiscountTiers) todavía no
                // cargaron: NO podemos afirmar "sin descuento" todavía (ver
                // iter170 — antes esto hacía que la tarjeta pareciera "sin
                // promo" hasta que /store-settings resolvía). Mostramos un
                // skeleton breve en su lugar.
                if (!settingsReady) {
                  return <div className="h-4 w-24 bg-slate-100 rounded animate-pulse" data-testid={`product-price-loading-${product.id}`} />;
                }
                const _rawTotal = typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0;
                const _winnerPct = getWinnerDiscountPct
                  ? Number(getWinnerDiscountPct(product, _rawTotal, currentQty || 1, selectedPaymentMethod) || 0)
                  : (getBestDiscountPct
                      ? Number(getBestDiscountPct(product, _rawTotal, currentQty || 1, selectedPaymentMethod) || 0)
                      : Number(user?.permanentDiscountPct || 0));
                if (_winnerPct > 0) {
                  const _listed = getListedPrice ? getListedPrice(basePrice, 0) : basePrice;
                  if (_listed <= currentPrice) return null;
                  return (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs line-through text-slate-400" data-testid={`product-listed-price-${product.id}`}>$ {_listed.toLocaleString('es-AR')}</span>
                      <span className="text-[9px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`product-discount-badge-${product.id}`}>-{Math.round(_winnerPct)}%</span>
                    </div>
                  );
                }
                // 🔥 Sin descuento ACTIVO todavía: si el admin activó la vista
                // previa del mayor descuento posible (Config. Tienda), le
                // mostramos al Comercio "hasta dónde puede llegar" el precio,
                // aunque el carrito no alcanzó ese objetivo. No depende del
                // método de pago (misma lógica que el descuento en vivo).
                if (isComercio && storeSettings?.showMaxDiscountPreview && getBestDiscountPct) {
                  const _previewPct = Number(getBestDiscountPct(product, 999999999999, currentQty || 1, null) || 0);
                  if (_previewPct > 0) {
                    const _topTier = (storeSettings?.cartDiscountTiers || [])
                      .slice().sort((a, b) => Number(b.pct || 0) - Number(a.pct || 0))[0];
                    const _previewPrice = Math.ceil(basePrice * (1 - _previewPct / 100));
                    return (
                      <div className="flex items-center gap-1 flex-wrap" data-testid={`product-max-discount-preview-${product.id}`}>
                        <span className="text-[9px] bg-orange-100 text-orange-700 font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          🔥 $ {_previewPrice.toLocaleString('es-AR')} desde ${Math.round(_topTier?.threshold || 0).toLocaleString('es-AR')}
                        </span>
                      </div>
                    );
                  }
                }
                return null;
              })()}
              <div className='flex items-baseline gap-1.5 flex-wrap'>
                <span className={`font-bold text-slate-800 ${currentTier ? 'text-base line-through opacity-40' : 'text-lg sm:text-xl'}`} style={{ fontFamily: 'Barlow Condensed' }}>
                  {!currentTier && <>$ {currentPrice.toLocaleString('es-AR')}</>}
                  {currentTier && <>{fmt(basePrice)}</>}
                </span>
                {currentTier && (
                  <span className='text-lg sm:text-xl font-black' style={{ color: '#D97706', fontFamily: 'Barlow Condensed' }} data-testid={`product-price-${product.id}`}>
                    {fmt(currentTier.priceNumeric)}
                  </span>
                )}
                {!currentTier && <span data-testid={`product-price-${product.id}`} className="hidden">{currentPrice}</span>}
              </div>
              <p className='text-[10px] text-slate-400'>{isPack ? `c/u (Pack ${packSize})` : 'por unidad'}</p>
            </div>
            {isOutOfStock ? (
              <div className='flex items-center justify-end gap-1'>
                <button disabled
                  className='px-3 py-2 text-[11px] font-bold rounded-lg bg-zinc-200 text-zinc-500 cursor-not-allowed'
                  data-testid={`btn-out-of-stock-${product.id}`}>
                  Sin stock
                </button>
              </div>
            ) : (
              <div className='flex items-center justify-end gap-1'>
                {currentQty > 0 && !editingQty && (
                  <>
                    <button onClick={handleRemove} className='w-9 h-9 flex items-center justify-center bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors text-base font-bold' data-testid={`product-decrease-${product.id}`}>
                      <Minus size={16} />
                    </button>
                    <button onClick={() => { setInputQty(currentQty.toString()); setEditingQty(true); }}
                      className='font-bold text-slate-800 min-w-[28px] px-1.5 py-1 text-center text-sm hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-0.5'
                      data-testid={`product-quantity-${product.id}`} title='Editar cantidad'>
                      {currentQty}
                      <Edit3 size={9} className='text-slate-300' />
                    </button>
                  </>
                )}
                {editingQty && (
                  <>
                    <input type='number' value={inputQty} onChange={(e) => setInputQty(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveQty()}
                      className='w-14 px-1 py-1.5 border rounded-lg text-center font-bold text-sm border-slate-300' autoFocus min='0' />
                    <button onClick={handleSaveQty} className='w-8 h-8 text-white rounded-lg' style={{ backgroundColor: theme.primary }}>
                      <Check size={14} className='mx-auto' />
                    </button>
                    <button onClick={() => setEditingQty(false)} className='w-8 h-8 bg-slate-100 text-slate-500 rounded-lg'>
                      <X size={14} className='mx-auto' />
                    </button>
                  </>
                )}
                {!editingQty && (
                  <button onClick={handleAdd}
                    className='w-9 h-9 flex items-center justify-center text-white rounded-lg transition-opacity hover:opacity-90'
                    style={{ backgroundColor: theme.primary }} data-testid={`product-increase-${product.id}`}>
                    <Plus size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
          {currentQty > 0 && (
            <p className='text-[10px] text-slate-400 mt-1 text-right'>
              Total: {fmt(currentPrice * currentQty)}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductCard;
